import React, { useState, useEffect } from 'react';
import { Building2, Cpu, TrendingUp, Home, HeartPulse, Zap, Factory, ShoppingBag, Layers } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import PageTitle from '../hooks/PageTitle';
import SectorsSkeletonLoader from '../components/skeletons/SectorsSkeletonLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Sectors = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/sectors/`);
      setSectors(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching sectors:', err);
      setError('Unable to load sectors. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const iconMap = {
    Building2,
    Cpu,
    TrendingUp,
    Home,
    HeartPulse,
    Zap,
    Factory,
    ShoppingBag,
    Layers
  };

  if (loading) {
    return <SectorsSkeletonLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
      <PageTitle title="Industry Sectors" />
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800">{error}</p>
            <button 
              onClick={fetchSectors}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Industry Sectors" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-serif mb-6">
              Industry Sectors
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Deep sector expertise combined with legal excellence to deliver commercially-focused solutions
            </p>
          </div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Our Sector Focus</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We bring first-hand industry knowledge and legal expertise to deliver tailored solutions across key sectors
            </p>
          </div>

          {sectors.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sectors available</h3>
              <p className="text-gray-600">Sectors will be published shortly</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {sectors.map((sector) => {
                const IconComponent = iconMap[sector.icon] || Building2;

                return (
                  <Card key={sector.id || sector._id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 group">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-6">
                        <div className="flex-shrink-0">
                          <div className="p-4 bg-gray-900 rounded-lg group-hover:bg-gray-800 transition-colors">
                            <IconComponent className="h-8 w-8 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-serif text-gray-900 mb-3 group-hover:text-gray-700 transition-colors">
                            {sector.name}
                          </h3>
                          <p className="text-gray-600 mb-4 leading-relaxed">
                            {sector.description}
                          </p>
                          {sector.expertise && sector.expertise.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                Key Areas of Expertise
                              </h4>
                              <div className="grid grid-cols-1 gap-2">
                                {sector.expertise.map((item, idx) => (
                                  <div key={idx} className="flex items-center text-sm text-gray-700">
                                    <div className="w-1.5 h-1.5 bg-gray-900 rounded-full mr-3"></div>
                                    {item}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-gray-900 mb-4">
            Industry-Focused Legal Excellence
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            Our sector-focused approach ensures we understand not just the law, but the commercial realities 
            and challenges facing your business.
          </p>
          <a 
            href="/contact" 
            className="inline-block px-8 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            Discuss Your Needs
          </a>
        </div>
      </section>
    </div>
  );
};

export default Sectors;
